import { Box, Spinner } from '@chakra-ui/react';
import { UploadSimple, File, X, CheckCircle } from '@phosphor-icons/react';

export interface FileUploadProgress {
  percentage: number;
  step?: string;
}

interface FileUploadCardProps {
  file: File | null;
  uploading: boolean;
  error: string | null;
  accept?: string;
  acceptLabel?: string;
  maxSizeMB?: number;
  progress?: FileUploadProgress | null;
  onChange: (file: File) => void;
  onReset: () => void;
}

export function getFileSize(size?: number): string {
  if (!size) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return `${(size / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

export default function FileUploadCard({
  file,
  uploading,
  error,
  accept = '.pdf,.txt',
  acceptLabel = 'PDF, or TXT',
  maxSizeMB = 5,
  progress,
  onChange,
  onReset
}: FileUploadCardProps) {
  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) onChange(selected);
  };

  if (file || uploading) {
    return (
      <Box display="flex" alignItems="center" width="100%">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="150px"
          height="150px"
          borderRadius="12px"
          border="1px solid"
          borderColor={error ? 'error.500' : 'primary.100'}
          bg="primary.50"
        >
          {uploading ? (
            <>
              <Spinner size="lg" borderWidth="4px" color="primary.900" />
              <Box mt="8px" fontSize="16px" color="neutrals.900" textAlign="center">
                {progress ? `${progress.percentage}%` : 'Uploading'}
              </Box>
            </>
          ) : (
            <File size={64} color="#4e76ff" />
          )}
        </Box>
        <Box ml="32px" color="neutrals.900">
          <Box display="flex" alignItems="center" mb="4px">
            {!uploading && (
              <Box mr="12px">
                <CheckCircle size={24} color="#4e76ff" weight="fill" />
              </Box>
            )}
            <Box fontSize="18px" color="neutrals.800" mr="16px">
              {file?.name}
            </Box>
            {!uploading && <X size={16} color="#545454" onClick={onReset} cursor="pointer" />}
          </Box>
          <Box fontSize="16px" color={error ? 'error.700' : 'neutrals.500'}>
            {getFileSize(file?.size)}
          </Box>
          {error && (
            <Box fontSize="16px" color="error.700" mt="4px">
              {error}
            </Box>
          )}
          {uploading && progress && (
            <Box mt="12px" width="240px">
              <Box width="100%" height="6px" bg="neutrals.200" borderRadius="full" overflow="hidden">
                <Box
                  height="100%"
                  bg="primary.500"
                  borderRadius="full"
                  width={`${progress.percentage}%`}
                  transition="width 0.3s ease"
                />
              </Box>
              {progress.step && (
                <Box fontSize="13px" color="neutrals.500" mt="6px">
                  {progress.step}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      width="100%"
      height="200px"
      border="2px dashed"
      borderColor="neutrals.300"
      borderRadius="12px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      _hover={{ borderColor: 'neutrals.300' }}
      bg="neutrals.50"
    >
      <input
        id="fileInput"
        type="file"
        accept={accept}
        style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
        onChange={onFileInputChange}
      />
      <Box display="flex" flexDirection="column" alignItems="center">
        <UploadSimple size={60} color="#404040" />
        <Box textAlign="center" color="neutrals.900">
          Drop file here or{' '}
          <Box as="span" color="primary.900">
            click to upload
          </Box>
        </Box>
        <Box color="neutrals.500" fontSize="14px">
          Upload {acceptLabel} file types ({maxSizeMB} MB max)
        </Box>
      </Box>
    </Box>
  );
}
